import { useEffect, useRef, useState, useCallback } from "react";
import axios from "axios";
import { Volume1, Volume2, VolumeX, Trophy, Heart, Zap } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = {
  bg: "#06070f",
  ball: "#22e6ff",
  coin: "#ffd43b",
  obstacles: ["#ff2d78", "#ff5e3a", "#b44bff"],
  grid: "rgba(34,230,255,0.05)",
};

export default function NeonDodge() {
  const wrapRef = useRef(null);
  const canvasRef = useRef(null);
  const rafRef = useRef(0);
  const audioRef = useRef(null);
  const mutedRef = useRef(localStorage.getItem("neon_dodge_mute") === "1");
  const volRef = useRef((() => { const v = parseFloat(localStorage.getItem("neon_dodge_vol")); return Number.isFinite(v) ? Math.min(1, Math.max(0, v)) : 0.7; })());
  const lastTickRef = useRef(0);
  const S = useRef(null);

  const [hud, setHud] = useState({ score: 0, lives: 3, level: 1 });
  const [screen, setScreen] = useState("menu"); // menu | playing | paused | over
  const [muted, setMuted] = useState(mutedRef.current);
  const [vol, setVol] = useState(volRef.current);
  const [board, setBoard] = useState([]);
  const [me, setMe] = useState(null);
  const [result, setResult] = useState(null);

  // ---------- audio ----------
  const beep = useCallback((freq, dur = 0.1, type = "square", vol = 0.08, slideTo = null, delay = 0) => {
    if (mutedRef.current || volRef.current <= 0) return;
    try {
      if (!audioRef.current) audioRef.current = new (window.AudioContext || window.webkitAudioContext)();
      const ctx = audioRef.current;
      if (ctx.state === "suspended") ctx.resume();
      const t0 = ctx.currentTime + delay;
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = type;
      o.frequency.setValueAtTime(freq, t0);
      if (slideTo) o.frequency.exponentialRampToValueAtTime(Math.max(20, slideTo), t0 + dur);
      g.gain.setValueAtTime(vol * volRef.current, t0);
      g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
      o.connect(g).connect(ctx.destination);
      o.start(t0);
      o.stop(t0 + dur + 0.02);
    } catch {}
  }, []);
  const sfx = {
    click: () => beep(520, 0.07, "square", 0.06, 760),
    coin: () => { beep(880, 0.07, "sine", 0.09); beep(1320, 0.09, "sine", 0.08, null, 0.06); },
    hit: () => { beep(200, 0.25, "sawtooth", 0.12, 55); },
    over: () => [392, 330, 262, 196].forEach((f, i) => beep(f, 0.18, "triangle", 0.1, null, i * 0.16)),
  };

  // ---------- leaderboard ----------
  const fetchBoard = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/funtime/neon-dodge/leaderboard`);
      setBoard(r.data.top || []);
      setMe(r.data.me || null);
    } catch {}
  }, []);
  useEffect(() => { fetchBoard(); }, [fetchBoard]);

  const submitScore = useCallback(async (finalScore) => {
    try {
      const r = await axios.post(`${API}/funtime/neon-dodge/score`, { score: finalScore });
      setResult(r.data);
      fetchBoard();
    } catch {
      setResult(null);
    }
  }, [fetchBoard]);

  // ---------- game state ----------
  const freshState = (W, H) => ({
    mode: "menu",
    W, H,
    ball: { x: W / 2, y: H - 70, tx: W / 2, ty: H - 70, r: 11 },
    trail: [],
    obstacles: [],
    coins: [],
    particles: [],
    stars: Array.from({ length: 50 }, () => ({ x: Math.random() * W, y: Math.random() * H, s: 0.3 + Math.random() * 1.2 })),
    score: 0,
    lives: 3,
    t: 0,
    lastSpawn: 0,
    lastCoin: 0,
    inv: 0,
    shake: 0,
    lastTime: performance.now(),
  });

  const resetRun = () => {
    const s = S.current;
    const { W, H, stars } = s;
    Object.assign(s, freshState(W, H), { stars, mode: "playing", lastTime: performance.now() });
    s.ball.x = s.ball.tx; s.ball.y = s.ball.ty;
    setHud({ score: 0, lives: 3, level: 1 });
    setResult(null);
  };

  // ---------- sizing ----------
  const fit = useCallback(() => {
    const wrap = wrapRef.current, canvas = canvasRef.current;
    if (!wrap || !canvas) return;
    const W = Math.max(320, wrap.clientWidth);
    const H = Math.min(600, Math.max(420, Math.round(W * 0.56)));
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    canvas.width = W * dpr; canvas.height = H * dpr;
    canvas.style.width = `${W}px`; canvas.style.height = `${H}px`;
    canvas.getContext("2d").setTransform(dpr, 0, 0, dpr, 0, 0);
    if (!S.current) S.current = freshState(W, H);
    else { S.current.W = W; S.current.H = H; }
  }, []);

  // ---------- main loop ----------
  useEffect(() => {
    fit();
    window.addEventListener("resize", fit);
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");

    const spawnObstacle = (s) => {
      const kind = Math.random() < 0.65 ? "rect" : "tri";
      const diff = Math.min(2.6, 1 + s.t / 40);
      const w = kind === "rect" ? 34 + Math.random() * 56 : 30 + Math.random() * 22;
      s.obstacles.push({
        kind,
        x: 20 + Math.random() * (s.W - 40 - w),
        y: -50,
        w,
        h: kind === "rect" ? 14 + Math.random() * 14 : w,
        vy: (105 + Math.random() * 80) * diff,
        rot: Math.random() * Math.PI,
        vr: (Math.random() - 0.5) * 2.4,
        color: COLORS.obstacles[(Math.random() * COLORS.obstacles.length) | 0],
      });
    };
    const spawnCoin = (s) => {
      s.coins.push({ x: 26 + Math.random() * (s.W - 52), y: -24, r: 9, vy: 90 + Math.random() * 60, ph: Math.random() * 6 });
    };
    const burst = (s, x, y, color, n = 14, speed = 160) => {
      for (let i = 0; i < n; i++) {
        const a = Math.random() * Math.PI * 2, v = speed * (0.4 + Math.random() * 0.8);
        s.particles.push({ x, y, vx: Math.cos(a) * v, vy: Math.sin(a) * v, life: 0.5 + Math.random() * 0.4, max: 0.9, color, r: 1.5 + Math.random() * 2.5 });
      }
    };

    const update = (s, dt) => {
      s.t += dt;
      // ball follows cursor
      const b = s.ball;
      b.x += (b.tx - b.x) * Math.min(1, dt * 11);
      b.y += (b.ty - b.y) * Math.min(1, dt * 11);
      b.x = Math.max(b.r, Math.min(s.W - b.r, b.x));
      b.y = Math.max(b.r, Math.min(s.H - b.r, b.y));
      s.trail.unshift({ x: b.x, y: b.y });
      if (s.trail.length > 12) s.trail.pop();

      // spawns, faster over time
      const spawnGap = Math.max(0.32, 1.05 - s.t * 0.014);
      if (s.t - s.lastSpawn > spawnGap) { s.lastSpawn = s.t; spawnObstacle(s); }
      if (s.t - s.lastCoin > 1.5) { s.lastCoin = s.t; spawnCoin(s); }

      s.score += dt * 6;
      if (s.inv > 0) s.inv -= dt;
      if (s.shake > 0) s.shake -= dt * 3;

      // obstacles
      for (let i = s.obstacles.length - 1; i >= 0; i--) {
        const o = s.obstacles[i];
        o.y += o.vy * dt; o.rot += o.vr * dt;
        if (o.y > s.H + 60) { s.obstacles.splice(i, 1); continue; }
        if (s.inv <= 0) {
          const rr = o.kind === "tri" ? o.w * 0.42 : Math.max(o.w, o.h) * 0.5 * 0.82;
          const dx = b.x - (o.x + o.w / 2), dy = b.y - (o.y + o.h / 2);
          if (dx * dx + dy * dy < (rr + b.r - 3) * (rr + b.r - 3)) {
            s.obstacles.splice(i, 1);
            s.lives -= 1; s.inv = 1.6; s.shake = 1;
            burst(s, b.x, b.y, o.color, 22, 220);
            if (s.lives <= 0) {
              s.mode = "over";
              const final = Math.floor(s.score);
              sfx.over();
              setScreen("over");
              setHud((h) => ({ ...h, lives: 0, score: final }));
              submitScore(final);
            } else sfx.hit();
          }
        }
      }
      // coins
      for (let i = s.coins.length - 1; i >= 0; i--) {
        const c = s.coins[i];
        c.y += c.vy * dt; c.ph += dt * 6;
        if (c.y > s.H + 30) { s.coins.splice(i, 1); continue; }
        const dx = b.x - c.x, dy = b.y - c.y;
        if (dx * dx + dy * dy < (c.r + b.r) * (c.r + b.r)) {
          s.coins.splice(i, 1);
          s.score += 25;
          burst(s, c.x, c.y, COLORS.coin, 12, 150);
          sfx.coin();
        }
      }
      // particles
      for (let i = s.particles.length - 1; i >= 0; i--) {
        const p = s.particles[i];
        p.life -= dt; if (p.life <= 0) { s.particles.splice(i, 1); continue; }
        p.x += p.vx * dt; p.y += p.vy * dt; p.vx *= 0.96; p.vy *= 0.96;
      }
    };

    const draw = (s) => {
      ctx.save();
      if (s.shake > 0) ctx.translate((Math.random() - 0.5) * 8 * s.shake, (Math.random() - 0.5) * 8 * s.shake);
      ctx.fillStyle = COLORS.bg;
      ctx.fillRect(-10, -10, s.W + 20, s.H + 20);
      // grid
      ctx.strokeStyle = COLORS.grid; ctx.lineWidth = 1;
      for (let x = 0; x < s.W; x += 40) { ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, s.H); ctx.stroke(); }
      for (let y = 0; y < s.H; y += 40) { ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(s.W, y); ctx.stroke(); }
      // stars
      ctx.fillStyle = "rgba(255,255,255,0.35)";
      for (const st of s.stars) {
        st.y += st.s * 0.4; if (st.y > s.H) { st.y = 0; st.x = Math.random() * s.W; }
        ctx.fillRect(st.x, st.y, 1.5, 1.5);
      }
      // coins
      for (const c of s.coins) {
        const pulse = 1 + Math.sin(c.ph) * 0.15;
        ctx.shadowColor = COLORS.coin; ctx.shadowBlur = 16;
        ctx.strokeStyle = COLORS.coin; ctx.lineWidth = 2.5;
        ctx.beginPath(); ctx.arc(c.x, c.y, c.r * pulse, 0, Math.PI * 2); ctx.stroke();
        ctx.fillStyle = "rgba(255,212,59,0.25)";
        ctx.beginPath(); ctx.arc(c.x, c.y, c.r * pulse * 0.55, 0, Math.PI * 2); ctx.fill();
      }
      // obstacles
      for (const o of s.obstacles) {
        ctx.save();
        ctx.translate(o.x + o.w / 2, o.y + o.h / 2);
        ctx.rotate(o.rot);
        ctx.shadowColor = o.color; ctx.shadowBlur = 18;
        ctx.strokeStyle = o.color; ctx.lineWidth = 2.5;
        ctx.fillStyle = `${o.color}22`;
        if (o.kind === "rect") {
          ctx.beginPath(); ctx.roundRect(-o.w / 2, -o.h / 2, o.w, o.h, 4); ctx.fill(); ctx.stroke();
        } else {
          const r = o.w / 2;
          ctx.beginPath();
          ctx.moveTo(0, -r); ctx.lineTo(r * 0.87, r * 0.5); ctx.lineTo(-r * 0.87, r * 0.5); ctx.closePath();
          ctx.fill(); ctx.stroke();
        }
        ctx.restore();
      }
      // trail + ball
      const b = s.ball;
      const visible = s.inv <= 0 || Math.floor(s.t * 12) % 2 === 0;
      if (visible) {
        for (let i = s.trail.length - 1; i > 0; i--) {
          const tr = s.trail[i], a = (1 - i / s.trail.length) * 0.3;
          ctx.fillStyle = `rgba(34,230,255,${a})`;
          ctx.beginPath(); ctx.arc(tr.x, tr.y, b.r * (1 - i / s.trail.length) * 0.9, 0, Math.PI * 2); ctx.fill();
        }
        ctx.shadowColor = COLORS.ball; ctx.shadowBlur = 22;
        ctx.fillStyle = COLORS.ball;
        ctx.beginPath(); ctx.arc(b.x, b.y, b.r, 0, Math.PI * 2); ctx.fill();
        ctx.shadowBlur = 0;
        ctx.fillStyle = "rgba(255,255,255,0.85)";
        ctx.beginPath(); ctx.arc(b.x - 3, b.y - 3, 3.5, 0, Math.PI * 2); ctx.fill();
      }
      // particles
      for (const p of s.particles) {
        ctx.globalAlpha = Math.max(0, p.life / p.max);
        ctx.shadowColor = p.color; ctx.shadowBlur = 10;
        ctx.fillStyle = p.color;
        ctx.beginPath(); ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2); ctx.fill();
      }
      ctx.globalAlpha = 1; ctx.shadowBlur = 0;
      ctx.restore();
    };

    let lastHudScore = -1, lastHudLives = -1, lastHudLevel = -1;
    const loop = () => {
      const s = S.current;
      if (s) {
        const now = performance.now();
        const dt = Math.min(0.05, (now - s.lastTime) / 1000);
        s.lastTime = now;
        if (s.mode === "playing") update(s, dt);
        draw(s);
        const sc = Math.floor(s.score), lvl = 1 + Math.floor(s.t / 15);
        if (sc !== lastHudScore || s.lives !== lastHudLives || lvl !== lastHudLevel) {
          lastHudScore = sc; lastHudLives = s.lives; lastHudLevel = lvl;
          setHud({ score: sc, lives: Math.max(0, s.lives), level: lvl });
        }
      }
      rafRef.current = requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener("resize", fit);
      if (audioRef.current) { audioRef.current.close().catch(() => {}); audioRef.current = null; }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---------- input ----------
  const onMove = (e) => {
    const s = S.current; if (!s) return;
    const rect = canvasRef.current.getBoundingClientRect();
    s.ball.tx = e.clientX - rect.left;
    s.ball.ty = e.clientY - rect.top;
  };
  const onClick = () => {
    const s = S.current; if (!s) return;
    sfx.click();
    if (s.mode === "menu" || s.mode === "over") { resetRun(); setScreen("playing"); }
    else if (s.mode === "playing") { s.mode = "paused"; setScreen("paused"); }
    else if (s.mode === "paused") { s.mode = "playing"; s.lastTime = performance.now(); setScreen("playing"); }
  };
  const toggleMute = (e) => {
    e.stopPropagation();
    mutedRef.current = !mutedRef.current;
    localStorage.setItem("neon_dodge_mute", mutedRef.current ? "1" : "0");
    setMuted(mutedRef.current);
    if (!mutedRef.current) beep(660, 0.08, "sine", 0.06);
  };
  const onVolChange = (e) => {
    const v = Math.min(1, Math.max(0, parseInt(e.target.value, 10) / 100));
    volRef.current = v;
    localStorage.setItem("neon_dodge_vol", String(v));
    setVol(v);
    if (mutedRef.current && v > 0) {
      mutedRef.current = false;
      localStorage.setItem("neon_dodge_mute", "0");
      setMuted(false);
    }
    const now = performance.now();
    if (now - lastTickRef.current > 150) { lastTickRef.current = now; beep(660, 0.05, "sine", 0.07); }
  };

  const Board = ({ compact }) => (
    <div className="w-full max-w-[280px]" data-testid="nd-leaderboard">
      <div className="flex items-center justify-center gap-1.5 text-amber-300 font-mono text-[11px] uppercase tracking-widest mb-1.5">
        <Trophy className="w-3.5 h-3.5" /> Top Players
      </div>
      {board.length === 0 ? (
        <p className="text-center font-mono text-[11px] text-cyan-200/50" data-testid="nd-leaderboard-empty">No scores yet — be the first!</p>
      ) : (
        board.slice(0, compact ? 5 : 10).map((r, i) => (
          <div key={i} className="flex items-center justify-between font-mono text-[11px] py-0.5 px-2 rounded" data-testid={`nd-board-row-${i + 1}`}
            style={{ background: i === 0 ? "rgba(255,212,59,0.1)" : "transparent" }}>
            <span className={i === 0 ? "text-amber-300" : "text-cyan-100/80"}>{i + 1}. {r.name}</span>
            <span className={i === 0 ? "text-amber-300" : "text-cyan-300"}>{r.best.toLocaleString()}</span>
          </div>
        ))
      )}
      {me && me.best > 0 && (
        <p className="text-center font-mono text-[10px] text-fuchsia-300/80 mt-1" data-testid="nd-my-rank">
          Your best: {me.best.toLocaleString()} (#{me.rank})
        </p>
      )}
    </div>
  );

  const overlayCls = "absolute inset-0 flex flex-col items-center justify-center gap-3 select-none";

  return (
    <div className="max-w-4xl" data-testid="neon-dodge-game">
      <div
        ref={wrapRef}
        className="relative rounded-xl overflow-hidden border border-cyan-500/30 cursor-crosshair"
        style={{ background: COLORS.bg, boxShadow: "0 0 40px rgba(34,230,255,0.12), inset 0 0 60px rgba(0,0,0,0.6)" }}
        onPointerMove={onMove}
        onClick={onClick}
        data-testid="nd-play-area"
      >
        <canvas ref={canvasRef} className="block" data-testid="nd-canvas" />

        {/* HUD */}
        <div className="absolute top-0 left-0 right-0 z-10 flex items-center justify-between px-3 py-2 pointer-events-none" data-testid="nd-hud">
          <div className="flex items-center gap-4 font-mono">
            <span className="text-cyan-300 text-sm font-bold tracking-wider" style={{ textShadow: "0 0 10px rgba(34,230,255,0.8)" }} data-testid="nd-score">
              {hud.score.toLocaleString()}
            </span>
            <span className="flex items-center gap-1" data-testid="nd-lives">
              {[0, 1, 2].map((i) => (
                <Heart key={i} className={`w-4 h-4 ${i < hud.lives ? "text-rose-400 fill-rose-500" : "text-slate-700"}`}
                  style={i < hud.lives ? { filter: "drop-shadow(0 0 4px rgba(255,45,120,0.8))" } : {}} />
              ))}
            </span>
            {screen === "playing" && (
              <span className="text-fuchsia-300 text-[10px] uppercase tracking-widest" data-testid="nd-level">Lv {hud.level}</span>
            )}
          </div>
          <div
            className="pointer-events-auto flex items-center gap-2 px-2 py-1.5 rounded-lg border border-cyan-500/30 bg-black/50"
            onClick={(e) => e.stopPropagation()}
            onPointerMove={(e) => e.stopPropagation()}
            data-testid="nd-sound-controls"
          >
            <input
              type="range"
              min="0"
              max="100"
              value={muted ? 0 : Math.round(vol * 100)}
              onChange={onVolChange}
              className="w-20 h-1 accent-cyan-400 cursor-pointer"
              data-testid="nd-volume-slider"
              aria-label="Sound volume"
            />
            <button
              onClick={toggleMute}
              className="p-0.5 rounded text-cyan-300 hover:text-cyan-100 transition-colors"
              data-testid="nd-mute-btn"
              aria-label={muted ? "Unmute" : "Mute"}
            >
              {muted || vol === 0 ? <VolumeX className="w-4 h-4" /> : vol < 0.5 ? <Volume1 className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
            </button>
          </div>
        </div>

        {/* START SCREEN */}
        {screen === "menu" && (
          <div className={overlayCls} style={{ background: "rgba(6,7,15,0.82)" }} data-testid="nd-start-screen">
            <Zap className="w-10 h-10 text-cyan-300" style={{ filter: "drop-shadow(0 0 12px rgba(34,230,255,0.9))" }} />
            <h2 className="font-mono text-3xl font-black tracking-[0.2em] text-cyan-300" style={{ textShadow: "0 0 20px rgba(34,230,255,0.9)" }}>
              NEON DODGE
            </h2>
            <div className="font-mono text-[11px] text-cyan-100/70 text-center leading-relaxed">
              Move the mouse — the ball follows your cursor.<br />
              Dodge the <span className="text-rose-400">neon shapes</span> · grab <span className="text-amber-300">coins</span> (+25) · 3 lives.<br />
              Left click = start · pause · restart.
            </div>
            <Board compact />
            <p className="font-mono text-sm text-emerald-300 animate-pulse mt-1" data-testid="nd-click-to-start">▶ CLICK TO START</p>
          </div>
        )}

        {/* PAUSE SCREEN */}
        {screen === "paused" && (
          <div className={overlayCls} style={{ background: "rgba(6,7,15,0.6)", backdropFilter: "blur(2px)" }} data-testid="nd-pause-screen">
            <h2 className="font-mono text-2xl font-black tracking-[0.25em] text-amber-300" style={{ textShadow: "0 0 16px rgba(255,212,59,0.8)" }}>
              PAUSED
            </h2>
            <p className="font-mono text-xs text-cyan-200/80 animate-pulse" data-testid="nd-click-to-resume">Click to resume</p>
          </div>
        )}

        {/* GAME OVER SCREEN */}
        {screen === "over" && (
          <div className={overlayCls} style={{ background: "rgba(6,7,15,0.85)" }} data-testid="nd-gameover-screen">
            <h2 className="font-mono text-3xl font-black tracking-[0.15em] text-rose-400" style={{ textShadow: "0 0 20px rgba(255,45,120,0.9)" }}>
              GAME OVER
            </h2>
            <p className="font-mono text-lg text-cyan-300" data-testid="nd-final-score">
              Score: <span className="font-black">{hud.score.toLocaleString()}</span>
            </p>
            {result?.is_new_best && (
              <p className="font-mono text-xs text-amber-300 animate-pulse" data-testid="nd-new-best">★ NEW PERSONAL BEST ★</p>
            )}
            <Board compact />
            <p className="font-mono text-sm text-emerald-300 animate-pulse mt-1" data-testid="nd-click-to-restart">▶ CLICK TO PLAY AGAIN</p>
          </div>
        )}
      </div>
      <p className="font-mono text-[11px] font-medium text-foreground/80 mt-2">
        Mouse only · click to start / pause / resume · scores post to the shared leaderboard automatically.
      </p>
    </div>
  );
}
